import React, {useRef, useState, useEffect} from 'react';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ArrowRightIcon,
  SparklesIcon,
} from '@heroicons/react/outline';

import Button, {ButtonTypes} from '../components/ButtonNew';
import FacePile from '../components/FacePile';
import {FadeOut} from '../components/FadeOut';
import {InterviewTypeIcon} from './InterviewTypeIcon';
import {InterviewTypes} from '../../../lib/constants/duet';
import {
  formatCompactSessionTime,
  formatLargeNum,
  clampLines,
  cx,
} from '../util';

export const InterviewTypeFilter = ({types, typeKey, onSelect}) => [
  {key: 'all', name: 'All'},
  ...types,
].map((type) => (
  <button
    key={type.key}
    type="button"
    onClick={() => onSelect(type.key)}
    className={cx(
      'px-4 py-1.5 rounded-full text-sm font-semibold border transition-colors whitespace-nowrap shrink-0',
      type.key === typeKey
        ? 'bg-indigo text-white border-indigo'
        : 'bg-white text-gray-700 border-gray-200 hover:border-gray-300',
    )}
  >
    {type.name}
  </button>
));

const CardShell = ({onClick, badge, children}) => (
  <div
    role="button"
    tabIndex={0}
    onClick={onClick}
    onKeyDown={(e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onClick(e);
      }
    }}
    className="relative flex flex-col cursor-pointer text-left shrink-0 w-72 bg-white border border-gray-200 rounded-2xl px-5 pt-4 pb-5 transition-shadow hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
  >
    {badge && (
      <span className="absolute -top-2 -right-2 whitespace-nowrap rounded-full bg-indigo-600 px-2 py-1 text-xs font-semibold text-white">
        {badge}
      </span>
    )}
    {children}
  </div>
);

const CardHeader = ({type}) => (
  <div className="flex items-center gap-3 mb-5">
    <InterviewTypeIcon typeKey={type.key} />
    <span className="min-w-0 text-base font-semibold text-gray-900 leading-tight">
      {type.name}
    </span>
  </div>
);

/**
 * One card per interview type, showing the next available peer session time and
 * opening the scheduling modal at the time-slot step with that time preselected.
 */
export const PeerMockCard = ({
  type, times, pendingRequests = [], onSelect,
}) => {
  const nextTime = times[0];
  const moreCount = Math.max(0, times.length - 1);
  const joinedCount = pendingRequests.length;

  return (
    <CardShell
      badge={type.badge}
      onClick={() => onSelect(type.key, nextTime)}
    >
      <CardHeader type={type} />
      <div className="text-xs font-semibold uppercase tracking-wide text-gray-400">
        Next session
      </div>
      <div className="text-lg font-bold text-gray-900 mt-1">
        {formatCompactSessionTime(nextTime)}
      </div>
      {moreCount > 0 && (
        <div className="text-sm font-normal text-gray-400 mt-0.5">
          {`+${moreCount} more time${moreCount === 1 ? '' : 's'} this week`}
        </div>
      )}
      {joinedCount > 0 && (
        <div className="flex items-center gap-3 my-4">
          <FacePile faces={pendingRequests} small borderColor="white" />
          <span className="text-sm text-gray-500">
            {`${joinedCount} joined`}
          </span>
        </div>
      )}
      <Button
        buttonType={ButtonTypes.SECONDARY}
        onClick={(e) => {
          e.stopPropagation();
          onSelect(type.key, nextTime);
        }}
        className="mt-auto w-full cursor-pointer"
      >
        Schedule now
        <ArrowRightIcon className="h-4 w-4 ml-1.5" />
      </Button>
    </CardShell>
  );
};

/**
 * One card per specific practice question, showing the question text, its
 * interview type, and the company logo it's been asked at; starts a
 * single-player AI session for that question.
 */
export const AIQuestionCard = ({question, onSelect}) => {
  const type = InterviewTypes[question.type];
  const {company, answeredCount} = question;

  return (
    <CardShell onClick={() => onSelect(question.type, question.slug)}>
      <div className="flex items-center gap-1.5 mb-3 min-w-0 text-xs text-gray-500">
        {company ? (
          <>
            <img
              className="h-4 w-4 object-contain shrink-0"
              src={company.imageUrl}
              alt={`${company.name} logo`}
            />
            <span className="truncate">{`Asked at ${company.name}`}</span>
          </>
        ) : (
          answeredCount >= 10 && (
            <span className="truncate">
              {`Answered ${formatLargeNum(answeredCount, true)}+ times this month`}
            </span>
          )
        )}
      </div>

      <div className="flex items-start gap-3 mb-8">
        <InterviewTypeIcon typeKey={question.type} />
        <div className="min-w-0 flex-1">
          <div
            className="text-base font-bold text-gray-900 leading-snug"
            style={clampLines(3)}
          >
            {question.title}
          </div>
          <div className="text-xs font-medium text-gray-500 mt-1">
            {type?.name}
          </div>
        </div>
      </div>

      <Button
        buttonType={ButtonTypes.SECONDARY}
        onClick={(e) => {
          e.stopPropagation();
          onSelect(question.type, question.slug);
        }}
        className="mt-auto w-full cursor-pointer"
      >
        <SparklesIcon className="h-4 w-4 mr-1.5" />
        Practice with AI
        <ArrowRightIcon className="h-4 w-4 ml-1.5" />
      </Button>
    </CardShell>
  );
};

/**
 * A non-animated, arrow-navigable carousel. The parent supplies the cards as
 * children (already filtered) and, optionally, a `filters` node rendered as a
 * chip row between the header and the cards.
 */
const DiscoveryCarousel = ({
  title,
  subtitle,
  align = 'left',
  filters,
  children,
}) => {
  const [canScroll, setCanScroll] = useState(false);
  const scrollRef = useRef(null);
  const alignRight = align === 'right';

  // Only show the scroll arrows when the cards actually overflow their
  // container, so a short list doesn't render dead arrows. Re-check on resize
  // and whenever the visible set of children changes.
  const itemsKey = React.Children.toArray(children).map((c) => c.key).join(',');
  useEffect(() => {
    const element = scrollRef.current;
    if (!element) return undefined;

    const checkScrollable = () => {
      setCanScroll(element.scrollWidth > element.clientWidth + 1);
    };
    checkScrollable();

    const resizeObserver = new ResizeObserver(checkScrollable);
    resizeObserver.observe(element);
    return () => resizeObserver.disconnect();
  }, [itemsKey]);

  const scrollBy = (dir) => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({left: dir * 320, behavior: 'smooth'});
    }
  };

  return (
    <section>
      <div className="max-w-6xl mx-auto px-4 lg:px-8">
        <div className={cx({'text-right': alignRight})}>
          <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">{title}</h2>
          {subtitle && (
            <p className={cx('mt-2 max-w-3xl text-base leading-7 text-gray-600', {'ml-auto': alignRight})}>
              {subtitle}
            </p>
          )}
        </div>

        {filters && (
          <FadeOut left right className="mt-4">
            <div className={cx('flex gap-2', {'justify-end': alignRight})}>
              {filters}
            </div>
          </FadeOut>
        )}
      </div>

      <div className="relative z-0 max-w-6xl mx-auto px-4 lg:px-8 mt-5">
        {canScroll && (
          <>
            <button
              type="button"
              aria-label="Scroll left"
              onClick={() => scrollBy(-1)}
              className="hidden md:flex absolute left-4 lg:left-8 top-1/2 -translate-x-1/2 -translate-y-1/2 z-20 h-10 w-10 rounded-full border border-gray-200 bg-white shadow-md items-center justify-center text-gray-600 hover:bg-gray-50"
            >
              <ChevronLeftIcon className="h-5 w-5" />
            </button>
            <button
              type="button"
              aria-label="Scroll right"
              onClick={() => scrollBy(1)}
              className="hidden md:flex absolute right-4 lg:right-8 top-1/2 translate-x-1/2 -translate-y-1/2 z-20 h-10 w-10 rounded-full border border-gray-200 bg-white shadow-md items-center justify-center text-gray-600 hover:bg-gray-50"
            >
              <ChevronRightIcon className="h-5 w-5" />
            </button>
          </>
        )}
        <FadeOut left right scrollRef={scrollRef}>
          <div className="flex gap-4 pt-2 pb-2">
            {children}
          </div>
        </FadeOut>
      </div>
    </section>
  );
};

export default DiscoveryCarousel;
